// Libraries
import clsx from "clsx";
import { useState } from "react";
import { Box, Checkbox, Grid, Typography } from "@mui/material";

// Layouts
import LabeledFormInput from "@/Layouts/components/SmartAddressFormSection/LabeledFormInput";
import SmartSelector from "@/Layouts/components/InputComponents/SmartSelector";
import SimpleTextField from "@/Layouts/components/InputComponents/SimpleTextField";

// Constants
import { PROSPECT_INFORMATION_TAB, REQUIRED_FIELD_CLASSES } from "../../../constants";

// Types & Interfaces
import { TType } from "../../../types";
import { TSelectValue } from "@/Layouts/components/InputComponents/SmartSelector/interfaces";

// Helpers
import { defineStatuses, isNewStatus, sortDropdownOptions } from "../../../helpers";
import { isTypeInternet } from "@/DataPages/context/crm/LeadsContext/helpers";

// Context
import { useLeadsContext } from "@/DataPages/context/crm/LeadsContext";

type TProspectInformationSectionProps = {
	salesPersonData: {
		data: TType[];
		loading: boolean;
	};
	franchises: string[];
	BDAs: TType[];
};

export default function ProspectInformationSection(props: Readonly<TProspectInformationSectionProps>) {
	const { salesPersonData, franchises, BDAs } = props;
	const { data: salesPersonOptions, loading: salesPersonLoading } = salesPersonData;

	const { isSavingLead, getContextValue, getContextError, onChangeContext } = useLeadsContext();

	/* States */
	const [statuses, setStatuses] = useState<TType[]>(defineStatuses(franchises));

	/* Helpers */
	function handleNewStatusAction(newValue: string) {
		if (!isNewStatus(newValue, statuses)) {
			resetAllDefaultStatuses();
			return;
		}

		const defaultTypeStatuses = defineStatuses(franchises).map((status) => status.type);
		const indexLastNewStatus = statuses.findIndex((status) => !defaultTypeStatuses.includes(status.type));

		const newStates = statuses;
		if (indexLastNewStatus === -1) {
			newStates.push({ type: newValue });
			setStatuses(newStates);
			return;
		}

		setStatuses(
			newStates.map((status, index: number) => {
				if (index === indexLastNewStatus) {
					return { type: newValue };
				}
				return status;
			})
		);
	}

	function resetAllDefaultStatuses() {
		const defaultStatuses = defineStatuses(franchises);
		setStatuses(defaultStatuses);
	}

	const isTypeInternetValue = isTypeInternet(getContextValue("prospect", "type") as string);

	return (
		<Box>
			<Typography className="!font-montserrat !font-[500] !text-[16px] !text-gray-text !mb-3">Prospect Information</Typography>

			<Grid container rowSpacing={1.5} spacing={3} className="!mb-3">
				<Grid item xs={12} sm={4}>
					<LabeledFormInput
						label="Category"
						formInput={
							<SmartSelector
								selectBy={"type"}
								showBy={"type"}
								options={sortDropdownOptions(PROSPECT_INFORMATION_TAB.categories)}
								selected={getContextValue("prospect", "category") as unknown as TSelectValue}
								popperProps={{ sx: { zIndex: `${1300}!important` } }}
								onChangeAction={(value) => onChangeContext("prospect", "category", (value as unknown as TType)?.type ?? "")}
								error={getContextError("prospect", "category")}
								inputProps={{
									placeholder: "Category",
								}}
								clearable
								disabled={isSavingLead}
							/>
						}
					/>
				</Grid>

				<Grid item xs={12} sm={4}>
					<LabeledFormInput
						label="Type"
						formInput={
							<SmartSelector
								selectBy={"type"}
								showBy={"type"}
								options={sortDropdownOptions(PROSPECT_INFORMATION_TAB.types)}
								selected={getContextValue("prospect", "type") as unknown as TSelectValue}
								popperProps={{ sx: { zIndex: `${1300}!important` } }}
								onChangeAction={(value) => onChangeContext("prospect", "type", (value as unknown as TType)?.type ?? "")}
								inputProps={{
									placeholder: "Type",
								}}
								error={getContextError("prospect", "type")}
								clearable
								disabled={isSavingLead}
							/>
						}
					/>
				</Grid>

				<Grid item xs={12} sm={4}>
					<LabeledFormInput
						label="Source (Internet Only)"
						formInput={
							<SimpleTextField
								editable
								outlined
								text={getContextValue("prospect", "providerName") as string}
								placeholder={"Prospect Source"}
								onFocusOut={(value) => onChangeContext("prospect", "providerName", value ?? "")}
								disabled={isSavingLead || !isTypeInternetValue}
								classes={clsx({
									[`${REQUIRED_FIELD_CLASSES}`]: getContextError("prospect", "providerName"),
								})}
								error={getContextError("prospect", "providerName")}
								inputProps={{ maxLength: 100 }}
							/>
						}
					/>
				</Grid>

				<Grid item xs={12} sm={4}>
					<LabeledFormInput
						label="Provider Service"
						formInput={
							<SimpleTextField
								editable
								outlined
								text={getContextValue("prospect", "providerService") as string}
								placeholder={"Provider Service"}
								onFocusOut={(value) => onChangeContext("prospect", "providerService", value ?? "")}
								disabled={isSavingLead || !isTypeInternetValue}
								inputProps={{ maxLength: 100 }}
							/>
						}
					/>
				</Grid>

				<Grid item xs={12} sm={4}>
					<LabeledFormInput
						label="Status"
						formInput={
							<SmartSelector
								addable={true}
								selectBy={"type"}
								showBy={"type"}
								options={sortDropdownOptions(statuses)}
								selected={getContextValue("prospect", "status") as unknown as TSelectValue}
								popperProps={{ sx: { zIndex: `${1300}!important` } }}
								onChangeAction={(value) => {
									handleNewStatusAction((value as unknown as TType)?.type ?? "");
									onChangeContext("prospect", "status", (value as unknown as TType)?.type ?? "");
								}}
								inputProps={{
									placeholder: "Status",
								}}
								error={getContextError("prospect", "status")}
								clearable
								disabled={isSavingLead}
							/>
						}
					/>
				</Grid>

				<Grid item xs={12} sm={4}>
					<LabeledFormInput
						label="Status Type"
						formInput={
							<SmartSelector
								selectBy={"type"}
								showBy={"type"}
								options={sortDropdownOptions(PROSPECT_INFORMATION_TAB.statusType)}
								selected={getContextValue("prospect", "statusType") as unknown as TSelectValue}
								popperProps={{ sx: { zIndex: `${1300}!important` } }}
								onChangeAction={(value) =>
									onChangeContext("prospect", "statusType", (value as unknown as TType)?.type ?? "")
								}
								inputProps={{
									placeholder: "Status Type",
								}}
								error={getContextError("prospect", "statusType")}
								clearable
								disabled={isSavingLead}
							/>
						}
					/>
				</Grid>

				<Grid item xs={12} sm={4}>
					<LabeledFormInput
						label="Salesperson"
						formInput={
							<SmartSelector
								selectBy={"type"}
								showBy={"type"}
								options={sortDropdownOptions(salesPersonOptions)}
								disabledOptions={
									getContextValue("prospect", "secondarySalesPerson")
										? [getContextValue("prospect", "secondarySalesPerson") as string]
										: []
								}
								selected={getContextValue("prospect", "salesPerson") as unknown as TSelectValue}
								popperProps={{ sx: { zIndex: `${1300}!important` } }}
								onChangeAction={(value) =>
									onChangeContext("prospect", "salesPerson", (value as unknown as TType)?.type ?? "")
								}
								inputProps={{
									placeholder: "Salesperson",
								}}
								error={getContextError("prospect", "salesPerson")}
								clearable
								loading={salesPersonLoading}
								disabled={isSavingLead}
							/>
						}
					/>
				</Grid>

				<Grid item xs={12} sm={4}>
					<LabeledFormInput
						label="Business Unit"
						formInput={
							<SmartSelector
								selectBy={"type"}
								showBy={"type"}
								options={sortDropdownOptions(BDAs)}
								selected={getContextValue("prospect", "businessUnitName") as unknown as TSelectValue}
								popperProps={{ sx: { zIndex: `${1300}!important` } }}
								onChangeAction={(value) =>
									onChangeContext("prospect", "businessUnitName", (value as unknown as TType)?.type ?? "")
								}
								inputProps={{
									placeholder: "Branch",
								}}
								error={getContextError("prospect", "businessUnitName")}
								clearable
								disabled={isSavingLead}
							/>
						}
					/>
				</Grid>

				<Grid item xs={12} sm={4} className="relative">
					<LabeledFormInput
						label="Customer Initiated"
						formInput={
							<Checkbox
								checked={getContextValue("prospect", "customerInitiated") as boolean}
								onChange={(_) => {
									const currentValue = getContextValue("prospect", "customerInitiated");
									onChangeContext("prospect", "customerInitiated", !currentValue);
								}}
								className="!ml-0 !mr-[210px] !mt-[-7px] !mb-[5px] !absolute"
								disabled={isSavingLead}
							/>
						}
					/>
				</Grid>
			</Grid>
		</Box>
	);
}
